<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card class="mx-auto" color="primary" variant="text" elevation="0"
            title="Comorbidity with certain diagnoses"
            ref="comorbidity-with-certain-diagnoses">
      <v-card-subtitle class="text-wrap">{{ comorbidityWithCertainDiagnosesSubtitle }}</v-card-subtitle>
      <v-row no-gutters>
        <v-col>
          <v-data-table :headers="comorbidityWithCertainDiagnosesHeaders"
                        :sort-by="comorbidityWithCertainDiagnosesSortBy"
                        :items="comorbidityWithCertainDiagnosesItems"
                        density="compact"
                        :fixed-header="true"
                        height="200px"
                        :items-per-page="0">
            <template #bottom></template>
            <template v-slot:no-data>
              <v-progress-circular color="primary" indeterminate></v-progress-circular>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col class="text-right">
          <vue-to-print :content="() => this.$refs['comorbidity-with-certain-diagnoses'].$el"
                        :document-title="icd + '-' + 'comorbidity-with-certain-diagnoses'"
                        remove-after-print>
            <template v-slot:trigger>
              <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
            </template>
          </vue-to-print>
        </v-col>
      </v-row>
    </v-card>
    <v-card class="mx-auto" color="primary" variant="text" elevation="0"
            title="Pain problems during follow-up time">
      <v-card-subtitle class="text-wrap">{{ painProblemsDuringFollowUpTimeDescription }}</v-card-subtitle>
      <v-row align="center" no-gutters>
        <v-col>
          <v-card class="mx-auto" min-height="800" elevation="0" title=""
                  ref="pain-problems-during-follow-up-time-chart">
            <v-row no-gutters>
              <v-col>
                <GChart type="ColumnChart"
                        @ready="painProblemsDuringFollowUpTimeChartReady"
                        :data="painProblemsDuringFollowUpTimeChartData"
                        :options="painProblemsDuringFollowUpTimeChartOptions"
                        :key="'painProblemsDuringFollowUpTimeChart' + painProblemsDuringFollowUpTimeChartKeyIndex"
                        v-if="painProblemsDuringFollowUpTimeChartData.length !== 0"/>
                <v-progress-circular color="primary" indeterminate v-else></v-progress-circular>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['pain-problems-during-follow-up-time-chart'].$el"
                              :document-title="icd + '-' + 'pain-problems-during-follow-up-time-chart'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
    <v-card class="mx-auto" color="primary" variant="text" elevation="0"
            title="Comorbidity"
            ref="comorbidity-with-diagnosis-groups">
      <v-card-subtitle class="text-wrap">{{ comorbidityWithDiagnosisGroupsSubtitle }}</v-card-subtitle>
      <v-row no-gutters>
        <v-col>
          <v-data-table :headers="comorbidityWithDiagnosisGroupsHeaders"
                        :sort-by="comorbidityWithDiagnosisGroupsSortBy"
                        :items="comorbidityWithDiagnosisGroupsItems"
                        density="compact"
                        :fixed-header="true"
                        height="800px"
                        :items-per-page="0">
            <template #bottom></template>
            <template v-slot:no-data>
              <v-progress-circular color="primary" indeterminate></v-progress-circular>
            </template>
            <template v-slot:[`item.diagnosisGroup`]="{ item }">
              <v-row v-for="diagnosisGroup in item.diagnosisGroup" :key="diagnosisGroup" no-gutters>
                {{ diagnosisGroup }}
              </v-row>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col class="text-right">
          <vue-to-print :content="() => this.$refs['comorbidity-with-diagnosis-groups'].$el"
                        :document-title="icd + '-' + 'comorbidity-with-diagnosis-groups'"
                        remove-after-print>
            <template v-slot:trigger>
              <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
            </template>
          </vue-to-print>
        </v-col>
      </v-row>
    </v-card>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";
import { VueToPrint } from "vue-to-print";

export default defineComponent({
  name: 'RareDiseaseComorbidity',
  props: {
    icd: String,
    icdDescription: String,
    visible: Boolean
  },
  components: {
    GChart,
    VueToPrint
  },
  data: () => ({
    comorbidityWithCertainDiagnosesSubtitle: '',
    comorbidityWithCertainDiagnosesHeaders: [
      {
        title: 'Diagnosis',
        align: 'start',
        key: 'diagnosis',
      },
      {
        title: 'Number of unique patients',
        align: 'start',
        key: 'numberOfUniquePatients',
      },
      {
        title: 'Patients at risk (N)',
        align: 'start',
        key: 'numberWithIcd',
      }
    ],
    comorbidityWithCertainDiagnosesSortBy: [{ key: 'diagnosis' }],
    comorbidityWithCertainDiagnosesItems: [],
    painProblemsDuringFollowUpTimeChartKeyIndex: 0,
    painProblemsDuringFollowUpTimeChartSelectedSeries: undefined,
    painProblemsDuringFollowUpTimeDescription: '',
    painProblemsDuringFollowUpTimeChartData: [],
    painProblemsDuringFollowUpTimeChartOptions: {
      backgroundColor: {
        fill: '#364c5b'
      },
      title: 'Pain problems during follow-up time',
      titlePosition: 'out',
      titleTextStyle: {
        color: '#ffffff',
        bold: false
      },
      height: 800,
      colors: ['#fb9a99', '#b2df8a', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7', '#a6cee3'],
      legend: {
        position: 'top',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      isStacked: true,
      series: {
        0: {
          targetAxisIndex: 1,
          type: 'line',
          pointShape: 'diamond',
          pointSize: 10
        },
        1: {
          targetAxisIndex: 0
        }
      },
      hAxis: {
        title: 'Year',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      vAxes: {
        0: {
          title: 'Number of unique patients',
          titleTextStyle: {
            color: '#f0f0f0',
            italic: false
          },
          gridlines: {
            color: '#364c5b',
            count: 0
          },
          minorGridlines: {
            color: '#364c5b',
            count: 0
          },
          textStyle: {
            color: '#ffffff',
            fontSize: 12
          }
        },
        1: {
          title: 'Patients at risk (N)',
          titleTextStyle: {
            color: '#f0f0f0',
            italic: false
          },
          gridlines: {
            count: 0
          },
          minorGridlines: {
            count: 0
          },
          textStyle: {
            color: '#ffffff',
            fontSize: 12
          }
        }
      },
      intervals: {
        'style': 'boxes',
        'boxWidth': 1,
        'lineWidth': 0,
        'fillOpacity': 0.2
      },
      tooltip: {
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    comorbidityWithDiagnosisGroupsSubtitle: '',
    comorbidityWithDiagnosisGroupsHeaders: [
      {
        title: 'Diagnosis group',
        align: 'start',
        key: 'diagnosisGroup'
      },
      {
        title: 'Number of registrations before Fabry diagnosis',
        align: 'start',
        key: 'numberOfUniqueRegistrationsBeforeDiagnosis'
      },
      {
        title: 'Number of registrations after Fabry diagnosis',
        align: 'start',
        key: 'numberOfUniqueRegistrationsAfterDiagnosis'
      },
      {
        title: 'Patients at risk (N)',
        align: 'start',
        key: 'patientsAtRisk'
      }
    ],
    comorbidityWithDiagnosisGroupsSortBy: [{ key: 'diagnosisGroup' }],
    comorbidityWithDiagnosisGroupsItems: [],
  }),
  watch: {
    painProblemsDuringFollowUpTimeChartSelectedSeries() {
      this.loadPainProblemsDuringFollowUpTimeChartData(
          this.painProblemsDuringFollowUpTimeChartSelectedSeries).then((response) => {
        this.painProblemsDuringFollowUpTimeChartData = response.data.data
        this.painProblemsDuringFollowUpTimeChartOptions.title = response.data.title
        this.painProblemsDuringFollowUpTimeDescription = response.data.description
      })
    },
  },
  methods: {
    forceRerender() {
      this.painProblemsDuringFollowUpTimeChartKeyIndex += 1;
    },
    async loadComorbidityWithCertainDiagnosesData() {
      return axios.get('rare-disease/comorbidity-with-certain-diagnoses',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadPainProblemsDuringFollowUpTimeChartData(painProblemGroup) {
      return axios.get('rare-disease/pain-problems-during-follow-up-time',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              pain_problem_group: painProblemGroup
            }
          });
    },
    async loadComorbidityWithDiagnosisGroupsData() {
      return axios.get('rare-disease/comorbidity-with-diagnosis-groups',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async painProblemsDuringFollowUpTimeChartReady(chart, google) {
      const setSeriesChartSelection = (selectedColumn) => {
        let selectedSeries = this.painProblemsDuringFollowUpTimeChartData[0][selectedColumn]
        if (this.painProblemsDuringFollowUpTimeChartSelectedSeries === selectedSeries) {
          this.painProblemsDuringFollowUpTimeChartSelectedSeries = undefined
        } else {
          this.painProblemsDuringFollowUpTimeChartSelectedSeries = selectedSeries
        }
      }

      const resetSeriesChartSelection = () => {
        this.painProblemsDuringFollowUpTimeChartSelectedSeries = undefined
      }

      const seriesSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          resetSeriesChartSelection()
          return
        }
        const selectedItem = selection[0];
        setSeriesChartSelection(selectedItem.column)
      }

      google.visualization.events.addListener(chart, 'select', seriesSelectHandler);
    }
  },
  async mounted() {
    await Promise.all([
      this.loadComorbidityWithCertainDiagnosesData(),
      this.loadPainProblemsDuringFollowUpTimeChartData(),
      this.loadComorbidityWithDiagnosisGroupsData()
    ]).then(responses => {
      this.comorbidityWithCertainDiagnosesItems = responses[0].data.data
      this.comorbidityWithCertainDiagnosesSubtitle = responses[0].data.comment
      this.painProblemsDuringFollowUpTimeChartData = responses[1].data.data
      this.painProblemsDuringFollowUpTimeChartOptions.title = responses[1].data.title
      this.painProblemsDuringFollowUpTimeDescription = responses[1].data.description
      this.comorbidityWithDiagnosisGroupsItems = responses[2].data.data
      this.comorbidityWithDiagnosisGroupsSubtitle = responses[2].data.comment
    })
  },
  updated() {
    if (this.visible) {
      this.forceRerender()
    }
  }
});
</script>

<style scoped>
@media print {
  .mx-auto {
    zoom: 50%;
  }
}
</style>
